import { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Bell, CheckCircle2, Loader2 } from "lucide-react";
import { API, IMAGES, img } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline, EASE } from "../components/motion";

const JoinList = () => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", route: "" });
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        source: "empty-legs",
        notes: `Empty leg alerts — route of interest: ${form.route || "any"}`,
      });
      setState("success");
    } catch {
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <motion.div
        data-testid="empty-leg-signup-success"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="border border-black/10 bg-white p-10"
      >
        <CheckCircle2 size={26} />
        <p className="mt-4 font-display text-2xl tracking-tight">You're on the list.</p>
        <p className="mt-3 text-sm leading-relaxed text-black/55">
          When a genuine empty leg is released through our operator network, we'll contact you.
        </p>
      </motion.div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-b border-black/15 py-3 text-[15px] placeholder:text-black/30 focus:border-black focus:outline-none transition-colors duration-300";

  return (
    <form onSubmit={submit} data-testid="empty-leg-signup-form" className="border border-black/10 bg-white p-8 sm:p-10">
      <div className="flex items-center gap-3">
        <Bell size={16} className="text-black/50" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/50">Empty leg alerts</p>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-black/60">
        Leave your details and preferred route. We'll contact you when a matching sector is released —
        no automated blasts.
      </p>
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <input required data-testid="empty-leg-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <input required type="email" data-testid="empty-leg-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <input data-testid="empty-leg-whatsapp" placeholder="WhatsApp (optional)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={inputCls} />
        <input data-testid="empty-leg-route" placeholder="Route of interest (e.g. Dubai → Maldives)" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} className={inputCls} />
      </div>
      {state === "error" && (
        <p data-testid="empty-leg-error" className="mt-4 text-sm text-red-700">
          Something went wrong — please try again.
        </p>
      )}
      <button
        type="submit"
        data-testid="empty-leg-join-submit"
        disabled={state === "submitting"}
        className="group mt-8 inline-flex items-center gap-3 bg-black px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-black/80 disabled:opacity-50"
      >
        {state === "submitting" ? (
          <>Joining <Loader2 size={14} className="animate-spin" /></>
        ) : (
          <>Join the list <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></>
        )}
      </button>
    </form>
  );
};

export default function EmptyLegsPage() {
  const [legs, setLegs] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/empty-legs`)
      .then((res) => setLegs(res.data))
      .catch(() => setLegs([]));
  }, []);

  return (
    <>
      <Seo
        title="Empty Legs — Private Jet Repositioning Sectors | JS Private Aviation"
        description="Genuine empty leg sectors released by our operator network. No manufactured inventory — join the alert list for your route."
      />
      <KineticHero
        testId="empty-legs-hero"
        fullHeight={false}
        overline="Empty Legs"
        lines={["Repositioning sectors,", "when they exist."]}
        sub="When an operator moves an aircraft without passengers, that sector can occasionally be chartered. We list genuine empty legs here as our network releases them — nothing manufactured."
        image={img(IMAGES.jetNight, 2000)}
      />

      <section className="bg-[#F9F9F6] py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <Overline>Current availability</Overline>
              <h2 className="mt-3 font-display text-3xl tracking-tight">Listed empty legs.</h2>

              {legs === null ? (
                <div className="mt-10 space-y-4" data-testid="empty-legs-loading">
                  {[0, 1].map((i) => (
                    <div key={i} className="h-24 animate-pulse border border-black/10 bg-white" />
                  ))}
                </div>
              ) : legs.length === 0 ? (
                <div className="mt-10 border border-black/10 bg-white p-10 sm:p-12" data-testid="empty-legs-none">
                  <p className="font-display text-2xl tracking-tight">No empty legs are currently listed.</p>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-black/55">
                    Empty legs appear without warning and disappear quickly — an aircraft repositioning for
                    its next charter. Rather than show fabricated availability, we publish only confirmed
                    sectors from our operator network.
                  </p>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-black/55">
                    Join the alert list and we'll contact you when a genuine sector matches your route.
                  </p>
                </div>
              ) : (
                <div className="mt-10 space-y-4" data-testid="empty-legs-list">
                  {legs.map((leg) => (
                    <div key={leg.id} className="border border-black/10 bg-white p-7" data-testid={`empty-leg-${leg.id}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <p className="font-display text-xl tracking-tight">
                          {leg.departure} <span className="text-black/30">→</span> {leg.destination}
                        </p>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">
                          {leg.date}{leg.time ? ` · ${leg.time}` : ""}
                        </span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[13px] text-black/55">
                        <span>{leg.aircraft}</span>
                        {leg.passenger_capacity && <span>{leg.passenger_capacity} passengers</span>}
                        {leg.price && <span>{leg.price}</span>}
                        <span className="text-black/40">{leg.availability}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-5">
              <JoinList />
              <p className="mt-6 text-[12px] leading-relaxed text-black/40">
                Empty legs are one-way sectors tied to an aircraft's existing schedule — dates, times and
                routing are fixed, and availability is confirmed only at booking.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
